/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../../design-system/base/TrueImpactText';
import Dashed from 'shared/statics/Dashed.png';
import DashedLightGrey from 'shared/statics/Dashed-light-grey.png';
import Solid from 'shared/statics/Solid.png';
import { COLORS } from '../../../design-system/settings/variables/Colors';
import { SPACERS } from '../../../design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCircleCheck,
  faCircleSmall,
  faHourglassClock,
  faPlus,
  faRedo,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactBulletPoint from '../../../design-system/components/TrueImpactBulletPoint';

interface IntroStepProps {
  reportYear?: any;
  className: string;
  heading: string;
  subheading: string;
  options: any;
  details: any;
  arrowDirection: string;
  time: {
    label: string;
    duration?: string;
  };
}

const StyledTimeIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: left;
  svg {
    width: 32px;
    height: 32px;
    margin-right: ${SPACERS.tiny};
  }
  .time-icon-text > * {
    margin: 0;
  }
  .time-icon-text {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.extraTiny};
  }
  .icons-wrapper {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.tiny};
    position: absolute;
    right: 0;
    top: 48px;
  }
`;

export const NextYearTimeIcon: React.FC<{
  time: IntroStepProps['time'];
  heading: IntroStepProps['heading'];
  arrowDirection: IntroStepProps['arrowDirection'];
  reportYear: IntroStepProps['reportYear'];
}> = ({ time, heading, arrowDirection, reportYear }) => {
  return (
    <StyledTimeIcon>
      <FontAwesomeIcon color={COLORS.ctaPrimary} icon={faHourglassClock} />
      <div className="time-icon-text">
        <TrueImpactText variant="heading-subheading">
          {reportYear} {heading}
        </TrueImpactText>
        <TrueImpactText color="greyDarkest">
          <strong>{time.label}:</strong> {time.duration}
        </TrueImpactText>
      </div>

      <div className="icons-wrapper">
        <FontAwesomeIcon
          icon={arrowDirection === 'right' ? faArrowRight : faRedo}
          className="next"
        />
        <FontAwesomeIcon
          icon={arrowDirection === 'right' ? faArrowRight : faRedo}
          className="next"
        />
        <FontAwesomeIcon
          icon={arrowDirection === 'right' ? faArrowRight : faRedo}
          className="next"
        />
      </div>
    </StyledTimeIcon>
  );
};

const StyledDiv = styled.div`
  display: flex;
  gap: ${SPACERS.medium};
  flex-direction: column;
  transition: all 0.5s;
  width: 320px;
  .step-wrapper {
    width: 320px;
    display: flex;
    height: 100%;
    flex-direction: column;
    background-size: contain;
    background-repeat: no-repeat;
    height: 360px;
    .step-heading-wrapper {
      text-align: center;
      .step-heading {
        margin-bottom: ${SPACERS.tiny};
      }
    }
    .capsule {
      margin-bottom: ${SPACERS.small};
      border: 1px solid ${COLORS.grayLight};
    }
    .step-option-wrapper {
      padding: ${SPACERS.medium} ${SPACERS.medium};
      height: 150px;
      width: 60%;
      display: flex;
      justify-content: center;
      flex-direction: column;
      gap: ${SPACERS.tiny};
      .list {
        svg {
          color: ${COLORS.brandPrimary};
        }
      }
      .single {
        flex-direction: column;
        gap: ${SPACERS.tiny};
        align-items: center;
        text-align: center;
        svg {
          width: 32px;
          height: 32px;
          color: ${COLORS.brandSecondary};
        }
      }
    }
    .step-details-wrapper {
      display: flex;
      flex-direction: column;
      gap: ${SPACERS.extraTiny};
      padding: ${SPACERS.small} ${SPACERS.medium};

      .description {
        padding-left: ${SPACERS.small};
      }
      .primary {
        svg {
          color: ${COLORS.brandPrimary};
        }
      }
      .secondary,
      .description {
        svg {
          color: ${COLORS.brandSecondary};
        }
      }
    }
  }
  &.template {
    svg {
      color: ${COLORS.brandPrimary};
    }
    .step-wrapper {
      background-image: url(${Solid});
    }
  }
  &.report {
    &.current {
      svg {
        color: ${COLORS.brandSecondary};
      }
      .step-wrapper {
        background-image: url(${Dashed});
      }
    }
    &.next {
      svg {
        color: ${COLORS.greyDark};
      }
      .step-wrapper {
        background-image: url(${DashedLightGrey});
        color: ${COLORS.brandSecondary};
        svg {
          color: ${COLORS.greyDark};
        }
      }
    }
  }
`;

const IntroStep: React.FC<IntroStepProps> = ({
  className,
  heading,
  subheading,
  options,
  details,
  arrowDirection,
  time,
  reportYear,
}) => {
  return (
    <StyledDiv className={className}>
      <NextYearTimeIcon
        time={time}
        heading={heading}
        arrowDirection={arrowDirection}
        reportYear={reportYear}
      />
      <div className="step-wrapper">
        <div className="step-option-wrapper">
          {options.map(x => (
            <TrueImpactBulletPoint
              key={x.label}
              text={x.label}
              icon={x.icon ? x.icon : faCircleCheck}
              className={options.length <= 1 ? 'single' : 'list'}
            />
          ))}
        </div>
        <div className="step-details-wrapper">
          <TrueImpactText variant="heading-subheading">
            {subheading}
          </TrueImpactText>
          {details.map(detail => (
            <>
              <TrueImpactBulletPoint
                key={detail.label}
                text={detail.label}
                icon={details.length < 3 ? faCircleCheck : faPlus}
                className={details.length < 3 ? 'secondary' : 'primary'}
              />
              {detail.description && (
                <TrueImpactBulletPoint
                  key={detail.description}
                  text={detail.description}
                  icon={faCircleSmall}
                  className={'description'}
                />
              )}
            </>
          ))}
        </div>
      </div>
    </StyledDiv>
  );
};

export default IntroStep;
